/**
 * `useSyncStatusStore` — Twin-route gTwin sync status state.
 *
 * Tracks four states (clean / dirty / saving / failed) plus a debounced
 * batcher that the AddLayer / SavePreset / patch flows can use to batch
 * mutations before persisting them via the scene-items adapter.
 *
 * The header `<SyncStatusBadge>` renders the state with both a colour
 * and a non-colour cue (Material Symbol + label) per design-policy §5.
 */

import { create } from 'zustand';
import {
  AnchorConflictError,
  InvalidSceneItemError,
  NotAuthorizedError,
  NotFoundError,
  StaleParentVersionError,
} from '@/sdk-stub/gtwin-sdk';

export type GtwinSyncState = 'clean' | 'dirty' | 'saving' | 'failed';

/**
 * WP-14 — classified error surface from the real platform backend.
 *
 * `kind` controls the colour, icon, and copy used by the
 * `<RealBackendBanner>`. Each kind maps to one of the typed errors the
 * platform SDK throws (see
 * `app-platform-gtwin/packages/sdk-ts/src/errors.ts`):
 *
 *   - `stale-parent`   -> `StaleParentVersionError` (409)
 *   - `anchor-conflict`-> `AnchorConflictError`     (409 anchor-conflict)
 *   - `invalid`        -> `InvalidSceneItemError`   (400 invalid-scene-item)
 *   - `permission`     -> `NotAuthorizedError`      (401 / 403)
 *   - `not-found`      -> `NotFoundError`           (404)
 *   - `server`         -> generic 5xx
 *   - `offline`        -> network failure (no Response)
 */
export type RealBackendErrorKind =
  | 'stale-parent'
  | 'anchor-conflict'
  | 'invalid'
  | 'permission'
  | 'not-found'
  | 'server'
  | 'offline';

export interface RealBackendError {
  readonly kind: RealBackendErrorKind;
  readonly message: string;
  readonly errorCode: string;
  readonly fieldPath?: string;
  readonly attemptedCrs?: string;
  readonly currentVersion?: number;
  readonly httpStatus?: number;
}

const ERROR_CODE_BY_KIND: Record<RealBackendErrorKind, string> = {
  'stale-parent': 'WP14-STALE-PARENT',
  'anchor-conflict': 'WP14-ANCHOR-CONFLICT',
  invalid: 'WP14-INVALID',
  permission: 'WP14-PERMISSION',
  'not-found': 'WP14-NOT-FOUND',
  server: 'WP14-SERVER',
  offline: 'WP14-OFFLINE',
};

interface SyncStatusState {
  status: GtwinSyncState;
  lastSavedAt: string | null;
  lastErrorMessage: string | null;
  pendingMutationCount: number;
  /** Classified error from the real backend (WP-14). `null` when clean. */
  realBackendError: RealBackendError | null;

  markDirty: (delta?: number) => void;
  markSaving: () => void;
  markClean: (savedAt?: string) => void;
  markFailed: (message: string) => void;
  resetSync: () => void;

  /**
   * WP-14 — classify an arbitrary error and store it as the most recent
   * real-backend failure. Safe to call with any thrown value; non-typed
   * errors fall back to `server` (HTTP-shape failures) or `offline`
   * (network-shape failures).
   */
  recordRealBackendError: (err: unknown) => RealBackendError;
  clearRealBackendError: () => void;
}

function classifyRealBackendError(err: unknown): RealBackendError {
  if (err instanceof StaleParentVersionError) {
    return {
      kind: 'stale-parent',
      message: err.message,
      errorCode: ERROR_CODE_BY_KIND['stale-parent'],
      currentVersion: err.currentVersion,
    };
  }
  if (err instanceof AnchorConflictError) {
    return {
      kind: 'anchor-conflict',
      message: err.message,
      errorCode: ERROR_CODE_BY_KIND['anchor-conflict'],
      attemptedCrs: err.attemptedCrs,
    };
  }
  if (err instanceof InvalidSceneItemError) {
    return {
      kind: 'invalid',
      message: err.message,
      errorCode: ERROR_CODE_BY_KIND.invalid,
      fieldPath: err.fieldPath,
    };
  }
  if (err instanceof NotAuthorizedError) {
    return {
      kind: 'permission',
      message: err.message,
      errorCode: ERROR_CODE_BY_KIND.permission,
    };
  }
  if (err instanceof NotFoundError) {
    return {
      kind: 'not-found',
      message: err.message,
      errorCode: ERROR_CODE_BY_KIND['not-found'],
    };
  }
  // Heuristic: messages produced by `throwForStatus` in
  // `sceneItemsReal.ts` start with `request failed: <status>`. Pull the
  // status out so the banner can render it; treat 5xx as `server` and
  // anything network-shaped (no status, name === 'TypeError', `Failed to
  // fetch`) as `offline`.
  const raw = err instanceof Error ? err.message : String(err);
  const match = /^request failed:\s+(\d{3})/.exec(raw);
  if (match) {
    const status = Number(match[1]);
    return {
      kind: status >= 500 ? 'server' : 'server',
      message: raw,
      errorCode: ERROR_CODE_BY_KIND.server,
      httpStatus: status,
    };
  }
  if (
    err instanceof TypeError ||
    /failed to fetch|networkerror|fetch failed|networkrequestfailed/i.test(raw)
  ) {
    return {
      kind: 'offline',
      message: raw,
      errorCode: ERROR_CODE_BY_KIND.offline,
    };
  }
  return {
    kind: 'server',
    message: raw,
    errorCode: ERROR_CODE_BY_KIND.server,
  };
}

export const useSyncStatusStore = create<SyncStatusState>((set) => ({
  status: 'clean',
  lastSavedAt: null,
  lastErrorMessage: null,
  pendingMutationCount: 0,
  realBackendError: null,

  markDirty: (delta = 1) =>
    set((state) => ({
      status: state.status === 'saving' ? 'saving' : 'dirty',
      pendingMutationCount: state.pendingMutationCount + delta,
      lastErrorMessage: null,
    })),

  markSaving: () =>
    set({
      status: 'saving',
      lastErrorMessage: null,
    }),

  markClean: (savedAt) =>
    set({
      status: 'clean',
      lastSavedAt: savedAt ?? new Date().toISOString(),
      lastErrorMessage: null,
      pendingMutationCount: 0,
      realBackendError: null,
    }),

  markFailed: (message) =>
    set({
      status: 'failed',
      lastErrorMessage: message,
    }),

  resetSync: () =>
    set({
      status: 'clean',
      lastSavedAt: null,
      lastErrorMessage: null,
      pendingMutationCount: 0,
      realBackendError: null,
    }),

  recordRealBackendError: (err) => {
    const classified = classifyRealBackendError(err);
    set({ realBackendError: classified });
    return classified;
  },

  clearRealBackendError: () => set({ realBackendError: null }),
}));

// ---------------------------------------------------------------------------
// Debounced batcher — used by visibility / opacity / patch flows so we do
// not write to the SDK on every keystroke.
// ---------------------------------------------------------------------------

export interface DebouncedBatcherOptions<T> {
  readonly waitMs: number;
  readonly flush: (batch: T[]) => Promise<void> | void;
  readonly onError?: (err: unknown) => void;
}

export interface DebouncedBatcher<T> {
  enqueue(item: T): void;
  flushNow(): Promise<void>;
  cancel(): void;
}

export function createDebouncedBatcher<T>(
  options: DebouncedBatcherOptions<T>,
): DebouncedBatcher<T> {
  let queue: T[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inflight: Promise<void> | null = null;

  const drain = async (): Promise<void> => {
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    useSyncStatusStore.getState().markSaving();
    try {
      await options.flush(batch);
      useSyncStatusStore.getState().markClean();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const store = useSyncStatusStore.getState();
      store.markFailed(message);
      // WP-14 — classify and surface real-backend failures through the
      // new `realBackendError` slice so `<RealBackendBanner>` can render
      // the typed error. This is always safe: the classifier returns
      // `server` / `offline` for non-typed errors, which the banner
      // surfaces with a generic "platform unavailable" copy.
      store.recordRealBackendError(err);
      options.onError?.(err);
      throw err;
    }
  };

  const scheduleFlush = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      inflight = drain().catch(() => {
        /* error already routed through onError */
      });
    }, options.waitMs);
  };

  return {
    enqueue(item: T): void {
      queue.push(item);
      useSyncStatusStore.getState().markDirty();
      scheduleFlush();
    },
    async flushNow(): Promise<void> {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (inflight) await inflight;
      inflight = drain();
      try {
        await inflight;
      } finally {
        inflight = null;
      }
    },
    cancel(): void {
      queue = [];
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
